import { z } from 'zod'
import { Model } from '../../_kernel/architecture'

import { Price } from '../../_kernel/Price'
import { ID } from '../../_kernel/ID'
import { KindOfAmount } from '../../_kernel/PriceType'
import { CancellationPolicy } from './CancellationPolicy'

export const AvailabilityValidation = z.object({
  id: z.instanceof(ID, { message: '[Availability] id is required' }),
  price: z.instanceof(Price, { message: '[Availability] price is required' }),
  cancellationPolicy: z.instanceof(CancellationPolicy).optional(),
})

export class Availability extends Model {
  static create({ id, price, cancellationPolicy }: z.infer<typeof AvailabilityValidation>) {
    AvailabilityValidation.parse({ id, price })
    return new Availability(id, price, cancellationPolicy)
  }

  static fromJSON(json: ReturnType<Availability['toJSON']>) {
    return Availability.create({
      id: ID.create({ value: json.id }),
      price: Price.fromJSON(json.price),
      ...(json.cancellationPolicy && {
        cancellationPolicy: CancellationPolicy.fromJSON(json.cancellationPolicy),
      }),
    })
  }

  static fromJSONOrUndefined(json: ReturnType<Availability['toJSON']> | undefined) {
    try {
      if (!json) return undefined

      return Availability.fromJSON(json)
    } catch {
      return undefined
    }
  }

  constructor(
    private readonly _id: ID,
    private readonly _price: Price,
    private readonly _cancellationPolicy?: CancellationPolicy
  ) {
    super()
  }

  get id() { return this._id.value } // prettier-ignore

  get price() { return this._price.toJSON() } // prettier-ignore

  get cancellationPolicy() { return this._cancellationPolicy?.toJSON() } // prettier-ignore

  toJSON() {
    return {
      id: this.id,
      price: this.price,
      ...(this.cancellationPolicy && { cancellationPolicy: this.cancellationPolicy }),
    }
  }

  hasPayNow(): boolean {
    return !!this.price?.payNow
  }

  hasCancellationPolicy(): boolean {
    return !!this._cancellationPolicy
  }

  getCancellationPolicyFromDateFormatted(locale: string) {
    return this._cancellationPolicy?.getFromDateFormatted(locale)
  }

  getPriceFormatted(locale: string, origin: KindOfAmount | 'priceWithoutDiscount' | 'payNow') {
    return this._price.getFormatted(locale, origin)
  }

  getTotalPriceCurrency() {
    return this._price.getMoney('total')?.currency
  }

  getTotalPriceAmount() {
    return this._price.getMoney('total')?.amount
  }

  getPayNowPriceAmount() {
    return this._price.getMoney('payNow')?.amount
  }
}
